import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Play, MessageCircle, ArrowRight, Eye, Check, TrendingUp, ListChecks } from 'lucide-react';
import { useLanguage, LanguageSelector } from '../components/LanguageContext';
import { openWhatsApp, WHATSAPP_NUMBER } from './shared/contact';
import TourBrainChat from './TourBrainChat';
import './AppTour.css';

// ============================================================
// APP TOUR — the pre-login answer to "what is actually in there?"
//
// Opened from the welcome screen, before any sign-in. A prospect or a newly
// invited owner has no account yet, so nothing in the product can show them
// what they are being given; this page does it instead.
//
// Five jobs, in the order a person asks them:
//   1. Show me       -> the screen recording, then four sample numbers
//   2. Why           -> three questions: what do I own, earn, need to do
//   3. Who does it   -> PROPERTY MANAGEMENT. The service is the product; the
//                       app is the window onto it. This is the section the
//                       page exists to sell, so it comes before the features.
//   4. What is in it -> app-only benefits, then the captioned screen tour
//   5. What happens  -> how onboarding actually goes, step by step
//
// The management detail is NOT re-written here. ServicesShowcase already has a
// full PM chapter (the scPm* keys: twelve service items in three groups, five
// stages, the honest note on what is and is not included) and this page reuses
// those same keys, then hands off to that tab via onSeePm. One copy of the
// pitch, so the two can never contradict each other on what we actually do.
//
// THE FILMS HAVE SOUND. The player starts them from a tap, which is what lets
// a browser play audio, so they are not muted.
//
// Language picks the film: an owner who chose Deutsch on the welcome screen
// gets the German film, English gets the English one.
// ============================================================

// The Asset Desk brand films (2026-10-23), from Downloads/AssetDesk-Film-
// EN-final.mp4 and AssetDesk-Film-DE-final-EN-Untertitel.mp4 (1920x1080,
// 91s, ~37MB each): scaled to 1280x720 at 30fps, CRF 24, AAC 128k, faststart
// — ~9.5MB each, because public/ also ships inside the store apps. Posters
// are the 3s frame (wordmark over the phone).
const FILM_EN = '/tour/assetdesk-film-en.mp4';
const FILM_DE = '/tour/assetdesk-film-de.mp4';
const POSTER_EN = '/tour/assetdesk-film-poster-en.jpg';
const POSTER_DE = '/tour/assetdesk-film-poster-de.jpg';
const FILM_RATIO = '16 / 9';

// Screen tour. Shots live in public/tour/shots — real captures of the owner
// portal on sample data, not mockups, so what a prospect is promised here is
// the screen they get.
// Bump when the shots are recaptured: the files keep their names, and hosting
// lets browsers cache images for an hour, so without it a returning visitor
// sees the old screens under the new captions.
const SHOTS_VERSION = '20261023';
const SHOTS: { img: string; title: string; caption: string }[] = [
  { img: '/tour/shots/dashboard.jpg', title: 'tourShotDashTitle', caption: 'tourShotDashCap' },
  { img: '/tour/shots/portfolio.jpg', title: 'tourShotPortTitle', caption: 'tourShotPortCap' },
  { img: '/tour/shots/valuation.jpg', title: 'tourShotValTitle', caption: 'tourShotValCap' },
  { img: '/tour/shots/report.jpg', title: 'tourShotRepTitle', caption: 'tourShotRepCap' },
  { img: '/tour/shots/pm-dashboard.jpg', title: 'tourShotPmTitle', caption: 'tourShotPmCap' },
  { img: '/tour/shots/property.jpg', title: 'tourShotUnitTitle', caption: 'tourShotUnitCap' },
  { img: '/tour/shots/lifecycle.jpg', title: 'tourShotLifeTitle', caption: 'tourShotLifeCap' },
  { img: '/tour/shots/tenant.jpg', title: 'tourShotTenantTitle', caption: 'tourShotTenantCap' },
  { img: '/tour/shots/signin.jpg', title: 'tourShotSignTitle', caption: 'tourShotSignCap' },
];

// The first thing after the film: what the owner Home actually looks like, in
// four numbers. Sample figures, and labelled as such on the page - a prospect
// should understand the product in five seconds, but never read these as a
// promise about their own property.
const KPIS: { label: string; value: string }[] = [
  { label: 'tourKpiValueLabel', value: 'tourKpiValue' },
  { label: 'tourKpiGrowthLabel', value: 'tourKpiGrowth' },
  { label: 'tourKpiRentLabel', value: 'tourKpiRent' },
  { label: 'tourKpiNextLabel', value: 'tourKpiNext' },
];

// The case, as the three questions an investor actually asks. Every line under
// a question is something the owner portal shows today - no companies, no
// refinancing, no co-owner splits until the product has them, because one
// claim the app cannot back up costs more trust than the section earns.
const QUESTIONS: { icon: React.ComponentType<any>; title: string; items: string[] }[] = [
  { icon: Eye, title: 'tourQ1Title', items: ['tourQ1a', 'tourQ1b', 'tourQ1c'] },
  { icon: TrendingUp, title: 'tourQ2Title', items: ['tourQ2a', 'tourQ2b', 'tourQ2c', 'tourQ2d'] },
  { icon: ListChecks, title: 'tourQ3Title', items: ['tourQ3a', 'tourQ3b', 'tourQ3c', 'tourQ3d'] },
];

// What the management service takes on, straight from the showcase's PM
// chapter. Three groups of four, in the order an owner lives them: get it
// earning, keep it right, then be left alone.
//
// Rendered as ticked cards rather than a bullet list: twelve services set as
// two columns of running text read as small print, and this is the section the
// page exists to sell. A card per service gives each one its own edge, and the
// tick says it is included rather than offered.
const PM_GROUPS: { title: string; items: { title: string; body: string }[] }[] = [
  {
    title: 'scPmGrp1Title',
    items: [
      { title: 'scPmB1Title', body: 'scPmB1' },
      { title: 'scPmB2Title', body: 'scPmB2' },
      { title: 'scPmB3Title', body: 'scPmB3' },
      { title: 'scPmB4Title', body: 'scPmB4' },
    ],
  },
  {
    title: 'scPmGrp2Title',
    items: [
      { title: 'scPmB5Title', body: 'scPmB5' },
      { title: 'scPmB6Title', body: 'scPmB6' },
      { title: 'scPmB7Title', body: 'scPmB7' },
      { title: 'scPmB8Title', body: 'scPmB8' },
    ],
  },
  {
    title: 'scPmGrp3Title',
    items: [
      { title: 'scPmB9Title', body: 'scPmB9' },
      { title: 'scPmB10Title', body: 'scPmB10' },
      { title: 'scPmB11Title', body: 'scPmB11' },
      { title: 'scPmB12Title', body: 'scPmB12' },
    ],
  },
];

// Benefits, numbered in the editorial hand the portfolio report uses. Kept
// strictly to what the SOFTWARE does — anything a person does for the owner
// belongs in PM_GROUPS above, or the page says the same thing twice.
const BENEFITS: { numeral: string; title: string; body: string }[] = [
  { numeral: 'I', title: 'tourBen1Title', body: 'tourBen1Body' },
  { numeral: 'II', title: 'tourBen2Title', body: 'tourBen2Body' },
  { numeral: 'III', title: 'tourBen3Title', body: 'tourBen3Body' },
  { numeral: 'IV', title: 'tourBen4Title', body: 'tourBen4Body' },
];

const STEPS: { title: string; body: string }[] = [
  { title: 'tourStep1Title', body: 'tourStep1Body' },
  { title: 'tourStep2Title', body: 'tourStep2Body' },
  { title: 'tourStep3Title', body: 'tourStep3Body' },
  { title: 'tourStep4Title', body: 'tourStep4Body' },
];

interface AppTourProps {
  /** back to the welcome screen */
  onClose: () => void;
  /** straight into owner sign-in, so the tour ends where it is meant to */
  onStart: () => void;
  /** opens the showcase on its Management tab, where the full service lives */
  onSeePm: () => void;
}

const AppTour: React.FC<AppTourProps> = ({ onClose, onStart, onSeePm }) => {
  const { t, language } = useLanguage();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  // The film's own ratio, read from its metadata. Until it loads the box uses
  // the CSS default, so the layout does not jump on a slow connection.
  const [filmRatio, setFilmRatio] = useState<string | null>(null);

  const film = language === 'de' ? FILM_DE : FILM_EN;
  const poster = language === 'de' ? POSTER_DE : POSTER_EN;

  // Switching language swaps the film. The <video> is remounted on `film` (see
  // its key), so it comes back at frame zero - drop back to the poster too,
  // otherwise the native controls sit over a film that silently reset.
  useEffect(() => {
    setPlaying(false);
  }, [film]);

  // The poster carries its own play affordance, so the native controls stay
  // hidden until the film is running - a control bar over a still frame reads
  // as a broken player.
  const start = () => {
    setPlaying(true);
    const v = videoRef.current;
    if (v) {
      // On a phone the film is as tall as the screen, so playing it from the
      // landing position leaves its caption band under the fold - the part
      // that carries the explanation. Bring it into view as it starts.
      v.closest('.tour-phone')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      v.play().catch(() => {
        /* a blocked autoplay still leaves the native controls to tap */
      });
    }
  };

  return (
    <div className="tour-root">
      {/* Always in reach: the AI section sits below the film and the sample
          numbers, where a first-time visitor did not find it. */}
      <button
        type="button"
        className="tour-ai-fab"
        onClick={() => document.getElementById('tour-ai')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
      >
        <MessageCircle size={18} />
        <span>{t('tourAiFab')}</span>
      </button>
      <header className="tour-bar">
        <button className="tour-back" onClick={onClose} type="button">
          <ArrowLeft size={17} strokeWidth={2} />
          <span>{t('tourBack')}</span>
        </button>
        <LanguageSelector compact />
      </header>

      {/* ---------- hero: the film ---------- */}
      <section className="tour-hero">
        <p className="tour-eyebrow">{t('tourEyebrow')}</p>
        <h1 className="tour-hero-title">{t('tourTitle')}</h1>
        <p className="tour-hero-sub">{t('tourSub')}</p>

        <div
          className="tour-phone tour-phone--wide"
          style={{ '--tour-film-ar': filmRatio || FILM_RATIO } as React.CSSProperties}
        >
          <div className="tour-phone-screen">
            <video
              ref={videoRef}
              key={film}
              className="tour-video"
              src={film}
              poster={poster}
              controls={playing}
              playsInline
              preload="metadata"
              onPlay={() => setPlaying(true)}
              onLoadedMetadata={(e) => {
                const v = e.currentTarget;
                if (v.videoWidth > 0 && v.videoHeight > 0) {
                  setFilmRatio(`${v.videoWidth} / ${v.videoHeight}`);
                }
              }}
            />
            {!playing && (
              <button className="tour-play" onClick={start} type="button">
                <span className="tour-play-disc">
                  <Play size={26} strokeWidth={1.6} fill="currentColor" />
                </span>
                <span className="tour-play-label">{t('tourPlay')}</span>
              </button>
            )}
          </div>
        </div>
      </section>

      {/* ---------- the result, in four numbers ---------- */}
      <section className="tour-kpis" aria-labelledby="tour-kpis-label">
        <p id="tour-kpis-label" className="tour-kpis-label">{t('tourKpiLabel')}</p>
        <dl className="tour-kpis-grid">
          {KPIS.map((k) => (
            <div key={k.label} className="tour-kpi">
              <dt>{t(k.label)}</dt>
              <dd>{t(k.value)}</dd>
            </div>
          ))}
        </dl>
        <p className="tour-kpis-note">{t('tourKpiNote')}</p>
      </section>

      {/* ---------- ask the AI: the app first, then the visitor's own plans ---------- */}
      <section className="tour-sec tour-sec--ai" id="tour-ai">
        <div className="tour-sec-head">
          <p className="tour-sec-kicker">{t('tourAiKicker')}</p>
          <h2 className="tour-sec-title">{t('tourAiTitle')}</h2>
          <p className="tour-sec-lede">{t('tourAiLede')}</p>
        </div>
        <TourBrainChat />
      </section>

      {/* ---------- why: three questions ---------- */}
      <section className="tour-sec tour-sec--why">
        <div className="tour-sec-head">
          <p className="tour-sec-kicker">{t('tourWhyKicker')}</p>
          <h2 className="tour-sec-title">{t('tourWhyTitle')}</h2>
          <p className="tour-sec-lede">{t('tourWhyLede')}</p>
        </div>

        <div className="tour-pillars">
          {QUESTIONS.map(({ icon: Icon, title, items }) => (
            <article key={title} className="tour-pillar">
              <span className="tour-pillar-mark">
                <Icon size={20} strokeWidth={1.7} />
              </span>
              <h3 className="tour-pillar-title">{t(title)}</h3>
              <ul className="tour-pillar-list">
                {items.map((k) => (
                  <li key={k}>{t(k)}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      {/* ---------- the service: this is what we are selling ---------- */}
      <section className="tour-sec tour-sec--pm">
        <div className="tour-sec-head">
          <p className="tour-sec-kicker">{t('scPmEyebrow')}</p>
          <h2 className="tour-sec-title">{t('tourPmFullTitle')}</h2>
          <p className="tour-sec-lede">{t('tourPmFullSub')}</p>
        </div>

        <div className="tour-pm-groups">
          {PM_GROUPS.map((g) => (
            <div key={g.title} className="tour-pm-group">
              <h3 className="tour-pm-group-title">{t(g.title)}</h3>
              <div className="tour-pm-tiles">
                {g.items.map((it) => (
                  <article key={it.title} className="tour-pm-tile">
                    <span className="tour-pm-tick" aria-hidden="true">
                      <Check size={13} strokeWidth={3} />
                    </span>
                    <h4 className="tour-pm-tile-title">{t(it.title)}</h4>
                    <p className="tour-pm-tile-body">{t(it.body)}</p>
                  </article>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* The twelve above are what we do; this is what they add up to for the
            owner. Kept to one line — a summary that needs a paragraph is not
            one. */}
        <div className="tour-pm-means">
          <p className="tour-pm-means-label">{t('tourPmMeansLabel')}</p>
          <p className="tour-pm-means-body">{t('tourPmMeans')}</p>
        </div>

        {/* Said plainly and early. An owner who discovers the service charge is
            theirs after signing feels sold to; one who reads it here does not. */}
        <p className="tour-pm-notice">{t('scPmNotice')}</p>

        <div className="tour-pm-more">
          <button className="tour-cta tour-cta--gold" type="button" onClick={onSeePm}>
            {t('tourPmSeeAll')}
            <ArrowRight size={16} strokeWidth={2} />
          </button>
        </div>
      </section>

      {/* ---------- what the app itself adds ---------- */}
      <section className="tour-sec tour-sec--benefits">
        <div className="tour-sec-head">
          <p className="tour-sec-kicker">{t('tourBenKicker')}</p>
          <h2 className="tour-sec-title">{t('tourBenTitle')}</h2>
          <p className="tour-sec-lede">{t('tourBenLede')}</p>
        </div>

        <div className="tour-ben-grid">
          {BENEFITS.map((b) => (
            <article key={b.numeral} className="tour-ben">
              <span className="tour-ben-num" aria-hidden="true">{b.numeral}</span>
              <div>
                <h3 className="tour-ben-title">{t(b.title)}</h3>
                <p className="tour-ben-body">{t(b.body)}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ---------- the screen tour ---------- */}
      <section className="tour-sec tour-sec--shots">
        <div className="tour-sec-head">
          <p className="tour-sec-kicker">{t('tourShotsKicker')}</p>
          <h2 className="tour-sec-title">{t('tourShotsTitle')}</h2>
          <p className="tour-sec-lede">{t('tourShotsLede')}</p>
        </div>

        {/* Swipe on a phone, grid on a desktop. The rail keeps a real screen
            legible at phone width - nine shots in a 2-column grid on a 390px
            screen renders every one of them too small to read. */}
        <div className="tour-rail">
          {SHOTS.map((s) => (
            <figure key={s.img} className="tour-shot">
              <div className="tour-shot-frame">
                <img src={`${s.img}?v=${SHOTS_VERSION}`} alt={t(s.title)} loading="lazy" />
              </div>
              <figcaption>
                <h3 className="tour-shot-title">{t(s.title)}</h3>
                <p className="tour-shot-cap">{t(s.caption)}</p>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* ---------- onboarding ---------- */}
      <section className="tour-sec tour-sec--steps">
        <div className="tour-sec-head">
          <p className="tour-sec-kicker">{t('tourStepsKicker')}</p>
          <h2 className="tour-sec-title">{t('tourStepsTitle')}</h2>
          <p className="tour-sec-lede">{t('tourStepsLede')}</p>
        </div>

        <ol className="tour-steps">
          {STEPS.map((s, i) => (
            <li key={s.title} className="tour-step">
              <span className="tour-step-num" aria-hidden="true">{i + 1}</span>
              <div>
                <h3 className="tour-step-title">{t(s.title)}</h3>
                <p className="tour-step-body">{t(s.body)}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* ---------- close ---------- */}
      <section className="tour-end">
        <div className="tour-rule" aria-hidden="true">
          <span />
          <i>&#9670;</i>
          <span />
        </div>

        <h2 className="tour-end-title">{t('tourEndTitle')}</h2>
        <p className="tour-end-sub">{t('tourEndSub')}</p>

        <div className="tour-end-actions">
          <button className="tour-cta tour-cta--gold" onClick={onStart} type="button">
            {t('tourEndSignIn')}
            <ArrowRight size={16} strokeWidth={2} />
          </button>
          <button
            className="tour-cta tour-cta--ghost"
            type="button"
            onClick={() => openWhatsApp(t('tourWaMessage'), WHATSAPP_NUMBER)}
          >
            <MessageCircle size={16} strokeWidth={2} />
            {t('tourEndAsk')}
          </button>
        </div>
      </section>
    </div>
  );
};

export default AppTour;
